<template>
  <div>
    <ion-item v-if="!editing">
      <ion-reorder slot="start"></ion-reorder>
      <ion-text :color="item.restricted ? 'danger' : ''">
        {{ item.name }}
        <span v-if="item.price" class="ion-margin-start">
          {{ item.price | usd }}
        </span>
      </ion-text>
      <span slot="end">
        <ion-button @click="edit()">Edit</ion-button>
        <ion-button @click="deleteConfirm()" color="danger" fill="outline">
          Delete
        </ion-button>
      </span>
    </ion-item>
    <div v-else class="editing">
      <ion-item>
        <ion-input
          :value="item.name"
          @input="item.name = $event.target.value.trim()"
          ref="input"
          :required="true"
          maxlength="255"
        ></ion-input>
        <span slot="end">
          <ion-button v-if="editing" @click="save()">Save</ion-button>
          <ion-button color="danger" fill="outline" @click="cancel()">
            Cancel
          </ion-button>
        </span>
      </ion-item>
      <ion-grid>
        <ion-row>
          <ion-col size="6">
            <ion-item>
              <ion-radio-group
                :value="priceType"
                @ionChange="priceType = $event.detail.value"
              >
                <ion-list-header>
                  <ion-label>Price</ion-label>
                </ion-list-header>
                <ion-item lines="none">
                  <ion-label>Item</ion-label>
                  <ion-radio slot="start" value="i"></ion-radio>
                </ion-item>
                <ion-item lines="none">
                  <ion-label>Per Option</ion-label>
                  <ion-radio slot="start" value="o"></ion-radio>
                </ion-item>
              </ion-radio-group>
            </ion-item>
          </ion-col>
          <ion-col size="6">
            <ion-item>
              <ion-label>
                Restricted
              </ion-label>
              <ion-toggle
                :checked="item.restricted"
                @ionChange="item.restricted = $event.detail.checked"
                color="danger"
              ></ion-toggle>
            </ion-item>
            <ion-item v-if="priceType == 'i'">
              <ion-input
                :value="item.price | centsToDollars"
                @change="
                  item.price = dollarsToCents($event.target.value.trim())
                "
                type="number"
                placeholder="#.##"
              >
                <span slot="start" class="ion-margin-start"
                  ><strong>$</strong></span
                >
              </ion-input>
            </ion-item>
          </ion-col>
        </ion-row>
      </ion-grid>
      <ion-list>
        <ion-item v-if="!item.options">
          <ion-button size="small" color="light" @click="addOption()">
            Add Option
          </ion-button>
        </ion-item>
        <div v-else>
          <ion-list-header>
            Option
          </ion-list-header>
          <ion-item>
            <ion-input
              ref="optionName"
              :value="item.options.name"
              @change="item.options.name = $event.target.value"
            ></ion-input>
            <ion-button
              slot="end"
              color="danger"
              fill="outline"
              @click="removeOption()"
            >
              Remove
            </ion-button>
          </ion-item>
          <ion-grid class="ion-padding-start ion-margin-start">
            <ion-row
              v-for="(value, index) in item.options.values"
              :key="`v-${value.id}`"
            >
              <ion-col>
                <ion-item>
                  <ion-input
                    :value="value.name"
                    :ref="'optionValue' + index"
                    @change="value.name = $event.target.value.trim()"
                  ></ion-input>
                  <ion-button
                    v-if="priceType == 'i'"
                    slot="end"
                    color="danger"
                    fill="outline"
                    @click="removeOptionValue(index)"
                  >
                    Remove
                  </ion-button>
                </ion-item>
              </ion-col>
              <ion-col v-if="priceType == 'o'">
                <ion-item>
                  <ion-input
                    :value="value.price | centsToDollars"
                    @change="
                      value.price = dollarsToCents($event.target.value.trim())
                    "
                    type="number"
                    placeholder="#.##"
                  >
                    <span slot="start"><strong>$</strong></span>
                  </ion-input>
                  <ion-button
                    slot="end"
                    color="danger"
                    fill="outline"
                    @click="removeOptionValue(index)"
                  >
                    Remove
                  </ion-button>
                </ion-item>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col>
                <ion-button
                  size="small"
                  color="light"
                  @click="addOptionValue()"
                >
                  Add Option Value
                </ion-button>
              </ion-col>
            </ion-row>
          </ion-grid>
        </div>
      </ion-list>
    </div>
  </div>
</template>

<script>
/**
 * One of the last components made during the time crunch. Probably could use some good TLC and refactoring
 *
 * One known issue: sometimes the formatting of the cents to dollars doesn't seem to update the display with the extra 0's... not sure why.
 * The value is set right
 */
const { v4: uuidv4 } = require("uuid");
import errorAlert from "@/ErrorAlert";
import { centsToDollars, dollarsToCents } from "@/util";

export default {
  props: {
    value: {
      type: Object,
      default: function() {
        return {
          name: "",
          price: ""
        };
      }
    },
    newItem: {
      type: Boolean,
      default: false
    }
  },

  data: function() {
    return {
      editing: false,
      item: { ...this.value },
      // this is only useful for the display
      priceType: this.value.hasOwnProperty("price") ? "i" : "o"
    };
  },

  mounted() {
    if (this.newItem) {
      this.edit();
    }
  },

  methods: {
    focus() {
      // for some reason, next tick doesn't find this right.
      setTimeout(() => {
        this.$refs.input.setFocus();
      }, 300);
    },

    edit() {
      this.editing = true;
      this.focus();
    },

    save() {
      // VALIDATION
      // very basic - and should be replaced
      if (!this.item.name) {
        return errorAlert("The item name is required.", this.focus);
      }
      if (this.item.hasOwnProperty("options")) {
        if (!this.item.options.name) {
          return errorAlert("An option name is required.");
        }
        if (!this.item.options.values.length) {
          return errorAlert("At least one value is required for options");
        }
        for (let i = 0; i < this.item.options.values.length; i++) {
          if (!this.item.options.values[i].name) {
            return errorAlert("All option values must have a name");
          }
        }
      }

      if (this.priceType == "i") {
        if (!this.item.price) {
          return errorAlert("A price is required");
        }
      } else {
        for (let i = 0; i < this.item.options.values.length; i++) {
          if (!this.item.options.values[i].price) {
            return errorAlert("All option values must have a price");
          }
        }
      }

      // DATA MANIPULATION
      if (this.priceType == "o") {
        this.$delete(this.item, "price");
      } else {
        // filter out the price on each option value
        if (this.item.hasOwnProperty("options")) {
          this.item.options.values.forEach(value => {
            this.$delete(value, "price");
          });
        }
      }

      this.editing = false;
      this.$emit("save", this.item);
    },

    deleteConfirm() {
      if (this.value.name) {
        return this.$ionic.alertController
          .create({
            header: "Delete Menu Item",
            subHeader: this.category,
            message:
              "Please confirm that you'd like to delete this menu item.  <strong class='text-color ion-color-danger'>This can not be undone!</strong>",
            buttons: [
              {
                text: "Cancel",
                role: "cancel",
                cssClass: "cancel-button"
              },
              {
                text: "Okay",
                cssClass: "okay-button",
                handler: () => {
                  this.deleteSelf();
                }
              }
            ]
          })
          .then(a => a.present());
      } else {
        // else means its a new item
        this.deleteSelf();
      }
    },

    deleteSelf() {
      this.$emit("delete");
    },

    cancel() {
      if (this.newItem) {
        this.deleteSelf();
      } else {
        this.editing = false;
        this.item = { ...this.value };
      }
    },

    // this just feels sloppy :(
    addOption() {
      this.$set(this.item, "options", {
        name: "",
        values: []
      });

      setTimeout(() => {
        this.$refs.optionName.setFocus();
      }, 300);
    },

    removeOption() {
      this.$delete(this.item, "options");
    },

    removeOptionValue(index) {
      this.item.options.values.splice(index, 1);
    },

    addOptionValue() {
      this.item.options.values.push({
        name: "",
        id: uuidv4(),
        price: "" // this will be filtered out on save
      });

      setTimeout(() => {
        // for some reason, this is rendering as an array and I'm not sure why
        const highest = this.item.options.values.length - 1;
        const refName = "optionValue" + highest;
        this.$refs[refName][0].setFocus();
      }, 300);
    },

    dollarsToCents
  },

  filters: {
    centsToDollars(v) {
      return v ? centsToDollars(v) : "";
    }
  }
};
</script>

<style lang="scss">
.editing {
  margin-bottom: 2rem;
  padding-left: 1rem;
  border-left: 2px solid #cccccc;
}
</style>
